import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Badge } from 'flowbite-react';
import { Change } from '../../models';
import { Icon, UserAvatar, Tooltip } from '..';
import {
    handleChangeMiddleClick,
    preventMiddleClickAutoscroll,
} from '../../utils/openChangeInNewBrowserTab';

interface ChangeCardProps {
    change: Change;
    showProject?: boolean;
    showSprint?: boolean;
    truncateTitle?: number;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    onSelect?: (change: Change, e: AnyObj) => void;
    showCreatedAt?: boolean;
    showUpdatedAt?: boolean;
    selectable?: boolean;
    selected?: boolean;
    onToggleSelect?: (change: Change, e: React.MouseEvent) => void;
}

function accentClass(change: Change): string {
    if (change.isFinished()) {
        return 'border-l-green-500';
    }
    if (change.isInReview()) {
        return 'border-l-blue-500';
    }
    if (change.deadline && change.deadline.isBefore(moment(), 'day') && !change.isFinished()) {
        return 'border-l-red-500';
    }
    if (change.isStarted()) {
        return 'border-l-indigo-500';
    }
    return 'border-l-gray-300';
}

function truncateText(text: string | null, maxLength: number): string | null {
    if (!text) {
        return null;
    }
    if (text.length <= maxLength) {
        return text;
    }
    return `${text.slice(0, maxLength)}…`;
}

function ChangeCard(props: ChangeCardProps) {
    const {
        change,
        showProject = true,
        showSprint = false,
        truncateTitle,
        className = '',
        onClick,
        showCreatedAt = false,
        showUpdatedAt = false,
        selectable = false,
        selected = false,
        onToggleSelect,
        onSelect,
    } = props;

    const { t, i18n } = useTranslation();
    const title = change.localizedTitle(i18n.language);
    const displayTitle = truncateTitle ? truncateText(title, truncateTitle) : title;
    const latestSprint = change.sprints?.length ? change.sprints[change.sprints.length - 1] : null;

    return (
        <div
            className={`group flex cursor-pointer flex-col gap-1 rounded-md border border-gray-200 bg-white px-3 py-2 shadow-sm transition hover:border-gray-300 hover:shadow dark:border-gray-700 dark:bg-gray-900 border-l-4 ${selected ? 'ring-2 ring-indigo-300' : ''} ${accentClass(change)} ${className}`}
            onClick={onClick}
            onMouseDown={preventMiddleClickAutoscroll}
            onAuxClick={(event) => handleChangeMiddleClick(event, change)}
        >
            <div className="flex items-start gap-2">
                {selectable && (
                    <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => {}}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleSelect?.(change, e);
                        }}
                        className="mt-1 h-4 w-4 shrink-0 cursor-pointer"
                    />
                )}
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                        <Badge color={change.color()} className="w-fit text-xs">
                            <Icon type={change.iconName()} size={3} className="inline" />
                            &nbsp;{change.slugName()}
                        </Badge>
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {displayTitle}
                        </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                        {showProject && change.project?.name && (
                            <span className="font-medium text-gray-600 dark:text-gray-400">
                                {change.project.name}
                            </span>
                        )}
                        {showSprint && latestSprint?.name && (
                            <span>{latestSprint.name}</span>
                        )}
                        {showCreatedAt && change.createdAt && (
                            <span>
                                <Icon type="date" size={3} className="mr-0.5 inline" />
                                {moment(change.createdAt).format('DD/MM/YYYY')}
                            </span>
                        )}
                        {showUpdatedAt && change.updatedAt && (
                            <span>
                                <Icon type="date" size={3} className="mr-0.5 inline" />
                                {moment(change.updatedAt).format('DD/MM/YYYY HH:mm')}
                            </span>
                        )}
                        {change.deadline && (
                            <span className={change.deadline.isBefore(moment(), 'day') ? 'text-red-600' : ''}>
                                <Icon type="date" size={3} className="mr-0.5 inline" />
                                {change.deadline.format('DD/MM/YYYY')}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                    <Tooltip content={t('changes.help.estimate') || ''} light>
                        <Badge color="warning" className="w-fit whitespace-nowrap text-xs">
                            {change.estimate}{t('changes.estimate_units_min')}
                        </Badge>
                    </Tooltip>
                    <Tooltip content={t('changes.help.priority') || ''} light>
                        <Badge color="indigo" className="w-fit text-xs">
                            <Icon type="priority" className="mr-0.5 inline" size={3} />
                            {change.priority}
                        </Badge>
                    </Tooltip>
                    {change.isFinished() && (
                        <Icon type="check" color="green-600" size={5} />
                    )}
                    {change.isInReview() && (
                        <Icon type="eye" color="blue-600" size={5} />
                    )}
                    {change.assignee?._id && (
                        <UserAvatar user={change.assignee} size={6} />
                    )}
                    {onSelect && (
                        <Badge
                            color="success"
                            className="text-xs"
                            onClick={(e: AnyObj) => {
                                e.stopPropagation();
                                onSelect(change, e);
                            }}
                        >
                            <Icon type="right" />
                        </Badge>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ChangeCard;
